const DESKTOP_MIN = 1100;
const CONNECTOR_BG = '#1F2E4C';
const CONNECTOR_LINE = 'rgba(0, 113, 189, 0.40)';
const CONNECTOR_LINE_WIDTH = 2;
const CONNECTOR_DOT_RADIUS = 6;

/**
 * @param {{ x: number, y: number }} center
 * @param {number} radius
 * @param {{ x: number, y: number }} target
 */
const pointOnCircleEdge = (center, radius, target) => {
	const dx = target.x - center.x;
	const dy = target.y - center.y;
	const length = Math.hypot(dx, dy);

	if (!length) {
		return center;
	}

	return {
		x: center.x + (dx / length) * radius,
		y: center.y + (dy / length) * radius,
	};
};

/**
 * @param {DOMRect} rect
 * @param {DOMRect} container
 */
const rectCenterInContainer = (rect, container) => ({
	x: rect.left + rect.width / 2 - container.left,
	y: rect.top + rect.height / 2 - container.top,
});

/**
 * @param {SVGElement} svg
 * @param {number} width
 * @param {number} height
 */
const setSvgViewBox = (svg, width, height) => {
	svg.setAttribute('viewBox', `0 0 ${width} ${height}`);
};

/**
 * @param {SVGElement} svg
 * @param {string} pathData
 * @param {{ stroke: string, strokeWidth: number }} options
 */
const appendPath = (svg, pathData, options) => {
	const path = document.createElementNS('http://www.w3.org/2000/svg', 'path');
	path.setAttribute('d', pathData);
	path.setAttribute('stroke', options.stroke);
	path.setAttribute('stroke-width', String(options.strokeWidth));
	path.setAttribute('fill', 'none');
	path.setAttribute('stroke-linecap', 'round');
	path.setAttribute('vector-effect', 'non-scaling-stroke');
	path.setAttribute('class', 'js-services-cap-line');
	svg.append(path);
	return path;
};

/**
 * @param {SVGElement} svg
 * @param {{ x: number, y: number }} point
 * @param {number} radius
 */
const appendDot = (svg, point, radius) => {
	const circle = document.createElementNS('http://www.w3.org/2000/svg', 'circle');
	circle.setAttribute('cx', point.x.toFixed(2));
	circle.setAttribute('cy', point.y.toFixed(2));
	circle.setAttribute('r', String(radius));
	circle.setAttribute('fill', CONNECTOR_BG);
	circle.setAttribute('class', 'js-services-cap-dot');
	svg.append(circle);
	return circle;
};

/**
 * @param {SVGElement} svg
 * @param {number} width
 * @param {number} height
 * @param {{ x: number, y: number }} hubEdge
 * @param {{ x: number, y: number }} anchorPoint
 */
const appendConnector = (svg, width, height, hubEdge, anchorPoint) => {
	setSvgViewBox(svg, width, height);

	const pathData = `M ${hubEdge.x.toFixed(2)} ${hubEdge.y.toFixed(2)} L ${anchorPoint.x.toFixed(2)} ${anchorPoint.y.toFixed(2)}`;

	appendPath(svg, pathData, {
		stroke: CONNECTOR_LINE,
		strokeWidth: CONNECTOR_LINE_WIDTH,
	});
	// Dot only at the card anchor — no circle on the hub ellipse.
	appendDot(svg, anchorPoint, CONNECTOR_DOT_RADIUS);
};

/** @type {WeakMap<HTMLElement, Set<(svg: SVGElement) => void>>} */
const redrawCallbacks = new WeakMap();

/**
 * @param {HTMLElement} diagram
 * @param {(svg: SVGElement) => void} callback
 */
export const onServicesCapabilitiesConnectorsRedraw = (diagram, callback) => {
	if (!redrawCallbacks.has(diagram)) {
		redrawCallbacks.set(diagram, new Set());
	}
	redrawCallbacks.get(diagram).add(callback);
};

/**
 * @param {HTMLElement} diagram
 * @param {(svg: SVGElement) => void} callback
 */
export const offServicesCapabilitiesConnectorsRedraw = (diagram, callback) => {
	redrawCallbacks.get(diagram)?.delete(callback);
};

/**
 * @param {HTMLElement} diagram
 */
export const redrawServicesCapabilitiesConnectors = (diagram) => {
	const body = diagram.querySelector('.services-cap-diagram__body');
	const svg = diagram.querySelector('.js-services-cap-lines');
	const hub = diagram.querySelector('.js-services-cap-hub');
	const anchors = diagram.querySelectorAll('.js-services-cap-anchor');

	if (!body || !svg || !hub || !anchors.length) {
		return;
	}

	svg.replaceChildren();

	if (diagram.dataset.layoutAnimating === 'true') {
		return;
	}

	if (window.innerWidth < DESKTOP_MIN || diagram.closest('[hidden]')) {
		return;
	}

	const bodyRect = body.getBoundingClientRect();

	if (!bodyRect.width || !bodyRect.height) {
		return;
	}

	const hubRect = hub.getBoundingClientRect();
	const hubCenter = rectCenterInContainer(hubRect, bodyRect);
	const hubRadius = hubRect.width / 2;

	anchors.forEach((anchor) => {
		const anchorRect = anchor.getBoundingClientRect();
		const anchorPoint = rectCenterInContainer(anchorRect, bodyRect);
		const hubEdge = pointOnCircleEdge(hubCenter, hubRadius, anchorPoint);

		appendConnector(svg, bodyRect.width, bodyRect.height, hubEdge, anchorPoint);
	});

	if (diagram.dataset.linesAnimated === 'true') {
		svg.querySelectorAll('.js-services-cap-line').forEach((path) => {
			const length = path.getTotalLength();
			path.style.strokeDasharray = String(length);
			path.style.strokeDashoffset = '0';
		});
	}

	redrawCallbacks.get(diagram)?.forEach((callback) => callback(svg));
};

/**
 * @param {HTMLElement} diagram
 */
const bindDiagram = (diagram) => {
	const body = diagram.querySelector('.services-cap-diagram__body');
	if (!body) {
		return;
	}

	const redraw = () => {
		window.requestAnimationFrame(() => redrawServicesCapabilitiesConnectors(diagram));
	};

	if (typeof ResizeObserver !== 'undefined') {
		const observer = new ResizeObserver(redraw);
		observer.observe(body);
	}

	window.addEventListener('resize', redraw, { passive: true });

	if (document.fonts?.ready) {
		document.fonts.ready.then(redraw);
	}

	redraw();
};

export const initServicesCapabilitiesConnectors = () => {
	document.querySelectorAll('.js-services-cap-diagram').forEach((diagram) => {
		bindDiagram(diagram);
	});
};
